// src/components/DashboardStudio/DashboardVersionHistoryPanel.tsx
// Version history side panel for the Dashboard Studio — mirrors FlowVersionHistory UX.
// Shows all published versions with timestamps, lets user load any snapshot.

import React, { useState, useEffect } from 'react';
import { apiFetch } from '@/api';
import { History, CheckCircle, Clock, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useTimezoneFormatter } from '@/hooks/useTimezoneFormatter';
import { notify } from '@/components/core';

interface DashboardVersion {
    id: string;
    dashboard_id: string;
    version_number: number;
    version: string | null;
    description: string | null;
    is_active: boolean;
    published_by: string | null;
    published_at: string;
    screenshot: string | null;
}

interface Props {
    dashboardId: string;
    isOpen: boolean;
    onClose: () => void;
    onLoadSnapshot?: (snapshot: any) => void; // optional: omit for read-only "See Versions" mode
}

export function DashboardVersionHistoryPanel({ dashboardId, isOpen, onClose, onLoadSnapshot }: Props) {
    const { formatDate } = useTimezoneFormatter();
    const [versions, setVersions] = useState<DashboardVersion[]>([]);
    const [loading, setLoading] = useState(false);
    const [restoring, setRestoring] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen && dashboardId) {
            fetchVersions();
        }
    }, [isOpen, dashboardId]);

    const fetchVersions = async () => {
        setLoading(true);
        try {
            const data = await apiFetch(`/dashboards/${dashboardId}/versions`);
            setVersions(data || []);
        } catch (e: any) {
            notify.error('Failed to load version history');
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async (version: DashboardVersion) => {
        setRestoring(version.id);
        try {
            const data = await apiFetch(`/dashboards/${dashboardId}/versions/${version.id}/restore`, null, { method: 'POST' });
            onLoadSnapshot?.(data.full_snapshot);
            notify.error(`Loaded snapshot from ${version.version || `v${version.version_number}`}. Review changes and click Save to keep.`);
            onClose();
        } catch (e: any) {
            notify.error(e.message || 'Failed to load snapshot');
        } finally {
            setRestoring(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[1200] flex"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="flex-1 bg-black/30 backdrop-blur-sm" />

            {/* Panel */}
            <div
                className="w-[380px] bg-white dark:bg-slate-900 h-full shadow-2xl flex flex-col border-l border-gray-200 dark:border-slate-700"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <History className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 dark:text-slate-100 text-sm">Version History</h3>
                            <p className="text-xs text-gray-500 dark:text-slate-400">{versions.length} published version{versions.length !== 1 ? 's' : ''}</p>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 text-gray-400 hover:text-gray-600">
                        <X className="w-4 h-4" />
                    </Button>
                </div>


                {/* Version list */}
                <div className="flex-1 overflow-y-auto py-3 px-4 space-y-2">
                    {loading ? (
                        <div className="flex flex-col gap-2 mt-4">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="h-20 rounded-xl bg-gray-100 dark:bg-slate-800 animate-pulse" />
                            ))}
                        </div>
                    ) : versions.length === 0 ? (
                        <div className="text-center py-12 text-gray-400 dark:text-slate-500">
                            <History className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">No published versions yet.</p>
                            <p className="text-xs mt-1">Publish this dashboard to create the first snapshot.</p>
                        </div>
                    ) : (
                        versions.map((v, idx) => (
                            <div
                                key={v.id}
                                className={`rounded-xl border transition-all duration-200 cursor-pointer ${
                                    selectedId === v.id
                                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                        : v.is_active
                                            ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10 hover:border-blue-300'
                                            : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/60 hover:border-blue-300 dark:hover:border-blue-600'
                                }`}
                                onClick={() => onLoadSnapshot && setSelectedId(prev => prev === v.id ? null : v.id)}
                            >
                                <div className="p-3">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-bold text-sm text-gray-800 dark:text-slate-100">
                                                    {v.version || `v${v.version_number}`}
                                                </span>
                                                {v.is_active && (
                                                    <Badge className="text-[10px] h-4 px-1.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700 gap-1">
                                                        <CheckCircle className="w-2.5 h-2.5" /> Active
                                                    </Badge>
                                                )}
                                                {idx === 0 && !v.is_active && (
                                                    <Badge variant="secondary" className="text-[10px] h-4 px-1.5">Latest</Badge>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-slate-500">
                                                <Clock className="w-3 h-3" />
                                                {formatDate(v.published_at)}
                                            </div>
                                            {v.published_by && (
                                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 truncate">
                                                    by {v.published_by}
                                                </p>
                                            )}
                                            {v.description && (
                                                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 line-clamp-2">{v.description}</p>
                                            )}
                                        </div>
                                        {selectedId === v.id && (
                                            <div className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <div className="w-2 h-2 rounded-full bg-white" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Footer */}
                {onLoadSnapshot ? (
                    <div className="border-t border-gray-100 dark:border-slate-800 px-4 py-3 space-y-2">
                        <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1.5 rounded-lg">
                            ⚠ Loading a snapshot replaces the current editor canvas. Edit and publish to save changes.
                        </p>
                        <Button
                            type="button"
                            className="w-full"
                            disabled={!selectedId || !!restoring}
                            onClick={async () => {
                                const v = versions.find(x => x.id === selectedId);
                                if (v) await handleRestore(v);
                            }}
                        >
                            {restoring ? 'Loading...' : 'Load into Editor'}
                        </Button>
                    </div>
                ) : (
                    <div className="px-4 py-3 border-t border-gray-100 dark:border-slate-800">
                        <Button variant="outline" size="sm" className="w-full" onClick={fetchVersions} disabled={loading}>
                            Refresh
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
